import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Star, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';

interface Nutritionist {
  id: string;
  name: string;
  bio: string | null;
  profile_image_url: string | null;
  rating_average: number;
  total_reviews: number;
  approach: string | null;
}

export default function NutritionistProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const [nutritionist, setNutritionist] = useState<Nutritionist | null>(null);
  const [loading, setLoading] = useState(true);

  const loadNutritionist = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('nutritionists')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setNutritionist(data as Nutritionist);
    } catch (error) {
      console.error('Error loading nutritionist:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadNutritionist();
    }
  }, [id, loadNutritionist]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!nutritionist) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900">Nutritionist Not Found</h2>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="flex-shrink-0">
                <div className="h-32 w-32 overflow-hidden rounded-full bg-gray-200">
                  {nutritionist.profile_image_url ? (
                    <img
                      src={nutritionist.profile_image_url}
                      alt={nutritionist.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-green-100 text-green-600">
                      <span className="text-4xl font-semibold">
                        {nutritionist.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-4">
                  <h1 className="text-3xl font-bold text-gray-900">{nutritionist.name}</h1>
                  <div className="mt-2 flex items-center gap-2">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="text-lg font-semibold text-gray-700">
                      {nutritionist.rating_average?.toFixed(1) || '0.0'}
                    </span>
                    <span className="text-gray-500">
                      ({nutritionist.total_reviews || 0} reviews)
                    </span>
                    <CheckCircle className="ml-2 h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-600">Verified</span>
                  </div>
                </div>
                {nutritionist.bio && (
                  <p className="mb-4 text-gray-600">{nutritionist.bio}</p>
                )}
                {nutritionist.approach && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900">Approach:</h3>
                    <p className="text-gray-600">{nutritionist.approach}</p>
                  </div>
                )}
                {profile?.role === 'client' && (
                  <div className="mt-6">
                    <Link
                      to={`/nutritionist/${id}/book`}
                      className="inline-block rounded-lg bg-green-600 px-6 py-3 text-sm font-semibold text-white hover:bg-green-700"
                    >
                      Book Consultation
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
